
import PageLayout from '@/components_v3/layout/PageLayout.vue';
import { Component, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import SvgCheckBtn from '@/assets/icons/check-btn.svg';
import SvgCancel from '@/assets/icons/cancel.svg';
import { IEmployeeAffiliateMethod } from '@/api/types/payout';
import {
    approveRequestAffiliateMethod,
    getOneEmployeeAffiliateMethod,
    makeDefaultAffiliateMethod,
    patchAffiliateMethodCommission,
    rejectRequestAffiliateMethod,
} from '@/api/payout';
import { showServerError } from '@/utils';
import { translatePayoutsRequestStatus } from '@/utils/translate';
import PayoutMethodInfo from '@/components/affiliate/affiliateMethods/PayoutMethodInfo.vue';
import Requisites from '@/components/affiliate/affiliateMethods/Requisites.vue';
import Status from '@/components/base/Status.vue';
import { NotificationMessage } from '@/utils/notification';
import { getWebmastersSearchGuid } from '@/api/orgstats';
import BaseButton from "@/components-UI/BaseButton.vue";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import SvgPen from "@/assets/icons/pencil.svg";
import SvgReload from "@/assets/icons/reload.svg";
import SvgCheck from "@/assets/icons/check.svg";
import {
    buildApproveAffiliateMethodPayload,
    canManagerApproveContractMethod,
    canManagerRejectContractMethod,
    CONTRACT_APPROVE_REQUISITES_HINT,
    isContractApproveDataMissing,
    isContractPayoutMethod,
} from '@/utils/payoutContractMethods';

enum STATUS {
    NEW = 'new',
    APPROVED = 'approved',
    PARTNER_REJECTED = 'partner_rejected',
    REJECTED = 'manager_rejected',
    EDITED = 'partner_edited',
}

@Component({
    components: {
        BaseButton,
        BaseTooltip,
        PageLayout,
        Status,
        SvgCheckBtn,
        SvgCancel,
        PayoutMethodInfo,
        Requisites,
        SvgPen,
        SvgReload,
        SvgCheck,
    },
})
export default class ChangeMethodRequest extends Vue {
    @Getter('authModule/GET_USER') getUser;
    employeeAffiliateMethod = {} as IEmployeeAffiliateMethod;
    reason = '';
    isEdit: boolean = false;

    translatePayoutsRequestStatus = translatePayoutsRequestStatus;
    contractApproveRequisitesHint = CONTRACT_APPROVE_REQUISITES_HINT;
    managerFieldsKeys = {};
    defaultSearch: string = '';
    personalCommissionValue: number | null = null;
    partnerCommissionValue: number | null = null;
    compensation: number | null = null;

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width < 576;
    }

    get isValid(): boolean {
        const isValidRole = this.getUser.roles.some(el => [
            'ROLE_SYSTEM',
            'ROLE_ADMIN',
            'ROLE_FINANCE_SUPERVISOR',
            'ROLE_FINANCIER',
            'ROLE_AFFILIATE_MANAGER',
        ].includes(el));

        const perCom = this.personalCommissionValue;
        const parCom = this.partnerCommissionValue;
        const validPerCom = perCom || perCom === 0;
        const validParCom= parCom || parCom === 0;
        const isValidCommission = (validPerCom || validParCom) ? validPerCom && validParCom : true;

        return this.isEdit ? isValidCommission : isValidRole;
    }

    get title(): string {
        const id = this.employeeAffiliateMethod?.id || '';
        return `Способ выплаты ${id}`;
    }

    get employeeAffiliateMethodID(): string {
        return this.$route.params.id;
    }

    get isShowEditFields(): boolean {
        return !this.isContractMethod &&
            this.employeeAffiliateMethod.method !== undefined &&
            this.employeeAffiliateMethod.method.manager_fields_required &&
                (this.employeeAffiliateMethod.status !== 'approved' &&
                    this.employeeAffiliateMethod.status !== 'deleted' &&
                        this.employeeAffiliateMethod.status !== 'partner_rejected');
    }

    get getMethodData(): IEmployeeAffiliateMethod {
        let data = {} as IEmployeeAffiliateMethod;

        if (!Array.isArray(this.employeeAffiliateMethod.partner_data) &&
            this.employeeAffiliateMethod.method?.partner_fields_required
        ) {
            data = { ...data, ...this.employeeAffiliateMethod.partner_data };
        }

        if (!Array.isArray(this.employeeAffiliateMethod.manager_data) &&
            this.employeeAffiliateMethod.method?.manager_fields_required &&
                (this.employeeAffiliateMethod.status === 'approved' ||
                    this.employeeAffiliateMethod.status === 'deleted' ||
                        this.employeeAffiliateMethod.status === 'partner_rejected')
        ) {
            data = { ...data, ...this.employeeAffiliateMethod.manager_data };
        }

        return data;
    }

    get isContractMethod(): boolean {
        return isContractPayoutMethod(this.employeeAffiliateMethod.method_id);
    }

    get approveButtonText(): string {
        return this.isContractMethod ? 'Одобрить' : 'Принять';
    }

    get isShowRejectBtn(): boolean {
        if (this.isContractMethod) {
            return canManagerRejectContractMethod(this.employeeAffiliateMethod.status);
        }

        return (
            this.employeeAffiliateMethod.status === STATUS.APPROVED
            || this.employeeAffiliateMethod.status === STATUS.NEW
            || this.employeeAffiliateMethod.status === STATUS.EDITED
        );
    }

    get isShowApproveBtn(): boolean {
        if (this.isContractMethod) {
            return canManagerApproveContractMethod(this.employeeAffiliateMethod.status);
        }

        return (
            this.employeeAffiliateMethod.status === STATUS.NEW
            || this.employeeAffiliateMethod.status === STATUS.EDITED
            || this.employeeAffiliateMethod.status === STATUS.REJECTED
        );
    }

    get isContractApproveDisabled(): boolean {
        return isContractApproveDataMissing(this.employeeAffiliateMethod);
    }

    get isNewRequest(): boolean {
        return this.employeeAffiliateMethod.status === STATUS.NEW
            || this.employeeAffiliateMethod.status === STATUS.EDITED ;
    }

    get isManagerFieldsKeys(): boolean {
        return !!this.employeeAffiliateMethod.method?.manager_fields_required;
    }

    created(): void {
        this.getAffiliateMethodData();
    }

    async getAffiliateMethodData(): Promise<void> {
        const ID: string = this.employeeAffiliateMethodID;
        try {
            this.employeeAffiliateMethod = await getOneEmployeeAffiliateMethod(ID);
            this.setPercentValues();

            if (Array.isArray(this.employeeAffiliateMethod.manager_data) &&
                this.employeeAffiliateMethod.method?.manager_fields_keys !== undefined
            ) {
                this.managerFieldsKeys = this.employeeAffiliateMethod.method?.manager_fields_keys.reduce((acc, el) => (acc[el] = '', acc), {});
            } else {
                this.managerFieldsKeys = this.employeeAffiliateMethod.manager_data;

                if (this.employeeAffiliateMethod.manager_data?.legal_id) {
                    const resp: any = await getWebmastersSearchGuid(this.employeeAffiliateMethod.manager_data?.legal_id);
                    this.defaultSearch = resp[0].name;
                }
            }
        } catch (err) {
            showServerError(err, 'Информация о способе выплаты не найдена');
        }
    }

    showNotification(text: string, type: string): void {
        new NotificationMessage(text, type);
    }

    async approveMethod(managerData = this.employeeAffiliateMethod.manager_data): Promise<void> {
        try {
            let response;

            if (this.isContractMethod) {
                const contractPayload = buildApproveAffiliateMethodPayload(this.employeeAffiliateMethod);
                if (!contractPayload) {
                    return;
                }

                response = await approveRequestAffiliateMethod(this.employeeAffiliateMethod.id, contractPayload);
            } else if (this.isManagerFieldsKeys) {
                response = await approveRequestAffiliateMethod(
                    this.employeeAffiliateMethod.id,
                    { data: { ...managerData.manager_data || managerData } },
                );
            } else {
                response = await approveRequestAffiliateMethod(this.employeeAffiliateMethod.id);
            }

            const { data } = response;
            this.showNotification('Способ выплаты подтверждён', 'success');
            this.changeMethod(data);
        } catch (err) {
            showServerError(err, 'Запрос на изменение способа выплат не подтвержден');
        }
    }

    async rejectMethod(): Promise<void> {
        try {
            const { data } = await rejectRequestAffiliateMethod(this.employeeAffiliateMethod.id, this.reason);
            this.showNotification('Способ выплаты отклонён','info');
            this.changeMethod(data);
        } catch (err) {
            showServerError(err, 'Запрос на изменение способа выплат не отклонен');
        }
    }
    async makeDefault(): Promise<void> {
        try {
            const { data } = await makeDefaultAffiliateMethod(this.employeeAffiliateMethod.id);
            this.showNotification('Способ выплаты назначен основным','info');
            this.changeMethod(data);
        } catch (err) {
            showServerError(err, 'Способ выплаты не назначен основным');
        }
    }

    changeMethod(data: IEmployeeAffiliateMethod): void {
        this.employeeAffiliateMethod.status = data.status;
        this.employeeAffiliateMethod.manager_id = data.manager_id;
        this.employeeAffiliateMethod.is_default = data.is_default;
        this.employeeAffiliateMethod.manager_data = data.manager_data;
    }

    changeManagerData(data: any): void {
        this.employeeAffiliateMethod.manager_data = {
            ...this.employeeAffiliateMethod.manager_data,
            ...data,
        };
    }

    setPercentValues(): void {
        const { commission: com, partner_commission: pCom, partner_compensation: cp } = this.employeeAffiliateMethod;
        this.personalCommissionValue = com ? com * 100 : com === 0 ? 0 : null;
        this.partnerCommissionValue = pCom ? pCom * 100 : pCom === 0 ? 0 : null;
        this.compensation = cp ? cp * 100 : 0;
    }

    async handleSaveEditFields(): Promise<void> {
        try {
            this.isEdit = !this.isEdit;

            if (!this.isEdit) {
                const com = this.personalCommissionValue;
                const pCom = this.partnerCommissionValue;
                const comp = this.compensation;
                const commission = com ? (com / 100).toFixed(4) : com;
                const commission_partner = pCom ? (pCom / 100).toFixed(4) : pCom;
                const compensation = comp ? (comp / 100).toFixed(4) : 0;
                await patchAffiliateMethodCommission(
                    this.$route.params.id,
                    {
                        commission,
                        commission_partner,
                        compensation,
                    },
                );
                await this.getAffiliateMethodData();
            }
        } catch (e) {
            showServerError(e, 'Комиссия не установлена');
        }
    }

    handleReloadClick(): void {
        this.isEdit = false;
        this.setPercentValues();
    }
}
